import { useEffect, useState } from 'react';
import _ from 'lodash';
import {
  ProcessingFeeOverride,
  ProcessingFeeOverrideConf,
  User,
  UserDocumentType,
} from '../../../clients/quipli-service/user/user';
import { Button } from '../../../components/form/button';
import { Modal } from '../../../components/modal/modal';
import { Spinner } from '../../../components/spinner/spinner';
import { Field, FileField } from '../../../components/field/field';
import { DatePicker } from '../../../components/date-picker/date-picker';
import { SelectForm } from '../../../components/select-form/select-form';
import { ValidationError } from '../../../components/validation-error/validation-error';
import { StateProvince } from '../../../clients/quipli-service/locales/locales';
import { DateTime } from 'luxon';
import { toast } from '../../../util/toasts';
import { useLocalesAPI } from '../../../clients/quipli-service/locales/locales-api-v1';
import classNames from 'classnames';
import { useUserAPI } from '../../../clients/quipli-service/user/user-api';
import { Typography } from '../../../components/typography/typography';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { UpsertRepresentativeRequest } from '../../../clients/quipli-service/representatives/representative';
import { ButtonGroup } from '../../../components/button-group/button-group';
import { Phone } from '../../../components/field/phone';
import { Checkbox } from '../../../components/checkbox/checkbox';
import { convertFiletoBase64 } from '../../../util/util';

export const CreateUserModal = ({
  isOpen,
  disableScanning = false,
  closeModal,
  setNewUser,
}: {
  isOpen: boolean;
  disableScanning?: boolean;
  closeModal: () => any;
  setNewUser?: (u: User) => void;
}) => {
  const { getConfiguration } = useAPI();

  const [formType, setFormType] = useState<'scan' | 'manual' | undefined>();
  const [enableLicenseScanning, setEnableLicenseScanning] = useState<boolean>(false);
  const [user, setUser] = useState<User>();
  const [modalTitle, setModalTitle] = useState<string>('Add New Customer');

  useEffect(() => {
    getConfiguration('CONF_ENABLE_HARDWARE_LICENSE_SCANNING')
      .then((config) => {
        const enableHardwareLicenseScanning = config?.value === '1' && !disableScanning;
        setEnableLicenseScanning(enableHardwareLicenseScanning);
        if (!enableHardwareLicenseScanning) {
          setFormType('manual');
        }
      })
      .catch(() => toast('Failed to fetch configuration', 'error'));
  }, []);

  const onUserScanSuccessful = (user: User) => {
    setUser(user);
    onSetFormType('manual');
  };

  const onUserScanFailed = () => {
    onSetFormType('manual');
  };

  const onCloseModal = () => {
    setFormType(enableLicenseScanning ? undefined : 'manual');

    if (user && setNewUser) {
      setNewUser(user);
    }

    setUser(undefined);
    closeModal();
  };

  const onSetFormType = (type: 'scan' | 'manual') => {
    if (type === 'scan') {
      setModalTitle("Scan Driver's License");
    } else if (formType === 'scan' && type === 'manual') {
      setModalTitle('Update Contact Information');
    } else {
      setModalTitle('Add New Customer');
    }

    setFormType(type);
  };

  return (
    <Modal isOpen={isOpen} title={modalTitle} close={onCloseModal}>
      {enableLicenseScanning && formType === undefined && (
        <div className="ml-auto mr-auto">
          <div className="mt-2 mb-2">
            <Button className="button button-primary w-full" onClick={() => onSetFormType('scan')}>
              <Typography type={'text-md'} className={'gap-[6px] py-[6px]'}>
                Scan From Driver's License
              </Typography>
            </Button>
          </div>

          <div className="mt-2 mb-2">
            <Button className="button button-primary w-full" onClick={() => onSetFormType('manual')}>
              <Typography type={'text-md'} className={'gap-[6px] py-[6px]'}>
                Manually Enter Customer Info
              </Typography>
            </Button>
          </div>
        </div>
      )}

      {formType === 'manual' && <ManualUserForm user={user} closeModal={onCloseModal} setNewUser={setNewUser} />}

      {formType === 'scan' && (
        <LicenseScanForm
          userScanSuccessful={onUserScanSuccessful}
          userScanFailed={onUserScanFailed}
          closeModal={onCloseModal}
        />
      )}
    </Modal>
  );
};

const ManualUserForm = ({
  user,
  closeModal,
  setNewUser,
}: {
  user?: User;
  closeModal: () => void;
  setNewUser?: (u: User) => void;
}) => {
  const { getConfiguration } = useAPI();
  const { createUser, updateUser } = useUserAPI();

  const [name, setName] = useState<string>('');
  const [company, setCompany] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [phoneIsValid, setPhoneIsValid] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [processingFeeOverrideConf, setProcessingFeeOverrideConf] = useState<ProcessingFeeOverrideConf>();
  const [processingFeeOverride, setProcessingFeeOverride] = useState<ProcessingFeeOverride>();
  const [withoutEmailChecked, setWithoutEmailChecked] = useState<boolean>(false);
  const [userType, setUserType] = useState<string>(user?.structureType ?? 'user');
  const [representativeName, setRepresentativeName] = useState<string>();
  const [representativePhone, setRepresentativePhone] = useState<string>('');
  const [representativePhoneIsValid, setRepresentativePhoneIsValid] = useState<boolean>(false);
  const [representativeTitle, setRepresentativeTitle] = useState<string>();
  const [representativeEmail, setRepresentativeEmail] = useState<string>();

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isRequestInProgress, setIsRequestInProgress] = useState<boolean>(false);

  const clearValues = () => {
    setName('');
    setCompany('');
    setPhone('');
    setEmail('');
    setWithoutEmailChecked(false);
    setUserType('user');
    setProcessingFeeOverride(undefined);
    closeModal();
  };

  const onSubmit = async () => {
    if (isDisabled) return;

    if (phone !== '' && !phoneIsValid) {
      toast('Please enter a valid phone number', 'error');
      return;
    }

    if (representativePhone !== '' && !representativePhoneIsValid) {
      toast('Please enter a valid representative phone number', 'error');
      return;
    }

    setIsRequestInProgress(true);
    try {
      const representatives: Omit<UpsertRepresentativeRequest, 'userId'>[] | undefined =
        userType == 'company' && representativeName
          ? [
              {
                name: representativeName,
                phone: representativePhone,
                title: representativeTitle,
                email:
                  representativeEmail && representativeEmail.length > 0 ? representativeEmail : 'customer@quipli.com',
              },
            ]
          : undefined;
      const newUser = await (user
        ? updateUser({ ...user, name, email: email.trim(), company, phone, processingFeeOverride })
        : createUser(
            name,
            withoutEmailChecked,
            userType,
            email.trim(),
            company,
            phone,
            representatives,
            processingFeeOverride,
          ));
      setNewUser && setNewUser(newUser);
      setIsRequestInProgress(false);
      onClose();
    } catch (e: any) {
      setIsRequestInProgress(false);
      if (e.message.msg) {
        toast(e.message.msg, 'error');
      } else if (e.message) {
        toast(e.message, 'error');
      }
    }
  };

  const onClose = () => {
    user ? closeModal() : clearValues();
  };

  useEffect(() => {
    getConfiguration('CONF_PROCESSING_FEE_OVERRIDE')
      .then((config) => {
        setProcessingFeeOverrideConf(config?.value as ProcessingFeeOverrideConf);
      })
      .catch(() => toast('Failed to fetch configuration', 'error'));
  }, []);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
      setCompany(user.company);
      setPhone(user.phone);
      setWithoutEmailChecked(false);
    }
  }, [user]);

  useEffect(() => {
    setIsDisabled(name.length === 0 || (!withoutEmailChecked && email.length === 0));
  }, [name, withoutEmailChecked, email]);

  const buttonItems: { label: string; value: 'user' | 'company' }[] = [
    { label: 'User', value: 'user' },
    { label: 'Company', value: 'company' },
  ];

  return (
    <div>
      <ButtonGroup<'user' | 'company'>
        items={buttonItems}
        onClick={(value) => setUserType(value as 'user' | 'company')}
      ></ButtonGroup>

      <Field
        label={`${userType == 'user' ? 'Full name' : 'Company name'}`}
        className="mt-2 mb-2"
        dataCy="full_name"
        type="text"
        value={name}
        onChange={setName}
        isRequired
      />

      {userType == 'user' && (
        <Field
          label="Company name"
          className="mb-2"
          dataCy="company_name"
          type="text"
          value={company}
          onChange={setCompany}
        />
      )}

      <Phone
        label="Phone number"
        className="mb-2"
        dataCy="phone_number"
        value={phone}
        onChange={setPhone}
        onChangeValidity={setPhoneIsValid}
      />

      <div className="flex flex-col gap-[6px]">
        <Field
          label="Email address"
          className="mb-2"
          dataCy="email_address"
          type="text"
          value={email}
          onChange={setEmail}
          isRequired={!withoutEmailChecked}
        />

        {!user && (
          <Checkbox
            type="white"
            className="mb-2"
            label={<span className="!text-grey-700">Does not have an email</span>}
            onChange={setWithoutEmailChecked}
            isChecked={withoutEmailChecked}
            dataCy="without_email"
          />
        )}
      </div>

      {!processingFeeOverrideConf ||
        (processingFeeOverrideConf !== ProcessingFeeOverrideConf.ApplyFee && (
          <Checkbox
            type="white"
            className="mb-2"
            label={
              <span className="!text-grey-700">
                {processingFeeOverrideConf == ProcessingFeeOverrideConf.OptionallyApplyFee
                  ? 'Waive Processing Fee'
                  : 'Apply Processing Fee'}
              </span>
            }
            onChange={(isChecked) => {
              isChecked
                ? setProcessingFeeOverride(
                    processingFeeOverrideConf == ProcessingFeeOverrideConf.OptionallyApplyFee
                      ? ProcessingFeeOverride.WaiveFee
                      : ProcessingFeeOverride.ApplyFee,
                  )
                : setProcessingFeeOverride(undefined);
            }}
            isChecked={
              !processingFeeOverride
                ? false
                : processingFeeOverrideConf === ProcessingFeeOverrideConf.OptionallyApplyFee
                ? processingFeeOverride == ProcessingFeeOverride.WaiveFee
                : processingFeeOverride == ProcessingFeeOverride.ApplyFee
            }
            dataCy="processing_fee_override"
          />
        ))}

      {userType == 'company' && !user && (
        <>
          <div className="mt-2 mb-2">
            <Typography type={'text-md'} className={'gap-[6px] py-[6px]'}>
              Primary Representative
            </Typography>
          </div>

          <Field
            type="text"
            label="Full name"
            className="mb-2"
            dataCy="full_name"
            value={representativeName}
            onChange={(name: string) => {
              setRepresentativeName(name);
            }}
            isRequired={true}
          />
          <Phone
            label="Phone number"
            className="mb-2"
            dataCy="phone_number"
            value={phone}
            onChange={setRepresentativePhone}
            onChangeValidity={setRepresentativePhoneIsValid}
          />
          <Field
            type="text"
            label="Title"
            className="mb-2"
            dataCy="representative_title"
            value={representativeTitle}
            onChange={(title: string) => {
              setRepresentativeTitle(title);
            }}
            isRequired={false}
          />

          <Field
            type="text"
            label="Email"
            className="mb-2"
            value={representativeEmail}
            onChange={(email: string) => {
              setRepresentativeEmail(email);
            }}
            isRequired={false}
            dataCy="representative_email"
          />
        </>
      )}

      {isRequestInProgress ? (
        <Spinner />
      ) : (
        <div className="flex flex-col lg:flex-row [&>*]:!grow gap-3 mt-4">
          <Button className="button button-secondary" onClick={() => closeModal()}>
            Cancel
          </Button>

          <Button
            className="button button-primary"
            id="submit"
            disabled={isDisabled || isRequestInProgress}
            onClick={onSubmit}
          >
            Save
          </Button>
        </div>
      )}
    </div>
  );
};

export const LicenseScanForm = ({
  userScanSuccessful,
  userScanFailed,
  closeModal,
  user,
}: {
  userScanSuccessful: (user: User) => void;
  userScanFailed: () => void;
  closeModal: () => void;
  user?: User;
}) => {
  const { getConfiguration } = useAPI();
  const { searchStateProvinces } = useLocalesAPI();
  const {
    createUserDocument,
    createUser,
    createUserDriverLicense,
    getUserDriverLicense,
    searchUsers,
    updateUserDriverLicense,
  } = useUserAPI();

  const [errors, setErrors] = useState<string[]>([]);
  const [isRequestInProgress, setIsRequestInProgress] = useState<boolean>(false);

  const [fullName, setFullName] = useState<string>('');
  const [dateOfBirth, setDateOfBirth] = useState<DateTime | undefined>();
  const [licenseNumber, setLicenseNumber] = useState<string>('');
  const [expirationDate, setExpirationDate] = useState<DateTime | undefined>();
  const [stateProvince, setStateProvince] = useState<StateProvince | undefined>();
  const [file, setFile] = useState<File>();

  const [stateProvinces, setStateProvinces] = useState<StateProvince[] | undefined>();

  const pageTitleKey = ' - [SCANNING]';

  useEffect(() => {
    const loadLocales = async () => {
      try {
        const countryConfig = await getConfiguration('CONF_COUNTRY');
        if (!countryConfig) {
          throw new Error('Failed to load country configuration.');
        }

        const states = await searchStateProvinces({ countryIds: [parseInt(countryConfig.value)] });
        setStateProvinces(states);
      } catch (e: any) {
        toast('Failed to load state/provinces.', 'error');
      }
    };

    loadLocales();

    // Add the title key to the page title so ParseLink selects the correct tab
    if (!document.title.includes(pageTitleKey)) {
      document.title = document.title + pageTitleKey;
    }
  }, []);

  const isSubmitDisabled = (): boolean => {
    return (
      fullName === '' ||
      dateOfBirth === undefined ||
      licenseNumber === '' ||
      expirationDate === undefined ||
      stateProvince === undefined ||
      file === undefined
    );
  };

  const submitRequest = async () => {
    // Validate inputs
    const errors: string[] = validateInputs();
    if (errors.length > 0) {
      setErrors(errors);
      return;
    }

    // Block buttons and exit of modal
    setIsRequestInProgress(true);

    const existingUser = user || (await findMatchingUser());
    if (existingUser) {
      // Add or update license for existing user
      const wasSuccessful = await updateExistingUser(existingUser);
      if (wasSuccessful) {
        userScanSuccessful(existingUser);
        return;
      }
    } else {
      // create new user and add license
      const newUser = await createNewUser();
      if (newUser) {
        userScanSuccessful(newUser);
        return;
      }
    }

    setIsRequestInProgress(false);

    // Reset fields and call failed callback
    resetTitle();
    resetFields();
    userScanFailed();
  };

  const validateInputs = () => {
    const errors: string[] = [];

    if (dateOfBirth !== undefined && dateOfBirth > DateTime.now()) {
      errors.push('Date of birth is in the future.');
    }

    if (expirationDate !== undefined && expirationDate < DateTime.now()) {
      errors.push('Expiration date is in the past.');
    }

    return errors;
  };

  const findMatchingUser = async (): Promise<User | undefined> => {
    if (licenseNumber === '' || stateProvince === undefined) {
      return;
    }

    try {
      const { items: matchingUsers } = await searchUsers({
        driversLicenseNumber: licenseNumber,
        driversLicenseStateId: stateProvince?.id,
        isDeleted: false,
      });

      if (matchingUsers.length === 0) {
        return;
      }

      return matchingUsers[0];
    } catch (e: any) {
      // Throw away error
      return;
    }
  };

  const uploadLicenseImage = async (userId: number) => {
    if (!file) {
      return;
    }

    await createUserDocument({
      userId,
      base64File: await convertFiletoBase64(file),
      documentType: UserDocumentType.License,
      fileType: file.type,
    }).catch(() => {
      toast('Failed to upload driver license image.', 'error');
    });
  };

  const updateExistingUser = async (user: User): Promise<boolean> => {
    try {
      // Upload the image file
      await uploadLicenseImage(user.id);

      const driversLicenseRequest = {
        fullName,
        dateOfBirth: dateOfBirth!,
        driverLicNum: licenseNumber,
        expirationDate: expirationDate!,
        stateId: stateProvince!.id,
      };

      const existingLicense = await getUserDriverLicense(user.id);
      if (existingLicense) {
        // Update existing license
        const updatedLicenseId = await updateUserDriverLicense(user.id, driversLicenseRequest);
        if (updatedLicenseId === 0) {
          toast("Failed to update user's driver's license.", 'error');
          return false;
        }

        return true;
      } else {
        // Create new license
        const licenseResponse = await createUserDriverLicense(user.id, driversLicenseRequest);
        if (licenseResponse.error) {
          toast(`Failed to create user's driver's license. ${licenseResponse.error}`, 'error');
          return false;
        }

        return true;
      }
    } catch (e: any) {
      toast("Failed to update user's driver's license.", 'error');
      return false;
    }
  };

  const createNewUser = async (): Promise<User | undefined> => {
    try {
      const user = await createUser(_.startCase(_.toLower(fullName)), true, 'user');
      if (!user) {
        toast('Failed to create user.', 'error');
        return;
      }

      // Upload the image file
      await uploadLicenseImage(user.id);

      // Create driver's license
      const licenseResponse = await createUserDriverLicense(user.id, {
        fullName,
        dateOfBirth: dateOfBirth!,
        driverLicNum: licenseNumber,
        expirationDate: expirationDate!,
        stateId: stateProvince!.id,
      });

      if (licenseResponse.error) {
        toast(`Failed to create user's driver's license. ${licenseResponse.error}`, 'error');
        return;
      }

      return user;
    } catch (e: any) {
      toast('Failed to create user.', 'error');
      userScanFailed();
      return;
    }
  };

  const onCloseModal = () => {
    resetTitle();
    resetFields();
    closeModal();
  };

  const resetTitle = () => {
    // Remove the title key from the page title
    if (document.title.includes(pageTitleKey)) {
      document.title = document.title.replace(pageTitleKey, '');
    }
  };

  const resetFields = () => {
    setFullName('');
    setDateOfBirth(undefined);
    setLicenseNumber('');
    setExpirationDate(undefined);
    setStateProvince(undefined);
    setFile(undefined);
    setErrors([]);
  };

  return (
    <div className="w-[350px]">
      <div className="text-center px-2 mb-4">
        <Typography type="text-sm" className="text-grey-500">
          Insert the driver's license into the scanner, and the scanner will automatically populate the fields below.
        </Typography>
      </div>

      <ValidationError className={classNames({ hidden: errors.length === 0 }, 'mb-2')}>
        {errors.map((error, i) => (
          <span key={i}>{error}</span>
        ))}
      </ValidationError>

      <Field
        type="text"
        label="Full Name"
        id="fullName"
        className="mb-2"
        value={fullName}
        onChange={setFullName}
        isRequired={true}
        autofocus={true}
      />

      <DatePicker
        label="Date of Birth"
        inputWrapperClassName="mb-2"
        isRequired={true}
        value={dateOfBirth}
        onChange={setDateOfBirth}
      />

      <Field
        type="text"
        label="License Number"
        id="licenseNumber"
        className="mb-2"
        value={licenseNumber}
        onChange={setLicenseNumber}
        isRequired={true}
      />

      <DatePicker
        label="Expiration Date"
        inputWrapperClassName="mb-2"
        isRequired={true}
        value={expirationDate}
        onChange={setExpirationDate}
      />

      <SelectForm
        label="Issuing State/Province"
        id="stateProvince"
        className="mb-2"
        menuShouldBlockScroll
        menuPosition="fixed"
        menuPortalTarget={document.body}
        options={stateProvinces}
        value={stateProvince}
        onChange={(option) => {
          setStateProvince(option ?? undefined);
        }}
        getOptionLabel={(o) => o.name}
        getOptionValue={(o) => `${o.id}`}
        isRequired={true}
        isDisabled={!stateProvinces}
        isLoading={!stateProvinces}
      />

      <FileField
        label="Front Image"
        id="file"
        className="mb-4"
        onChange={(file) => setFile(file as File)}
        value={file}
        isRequired={true}
      />

      {isRequestInProgress ? (
        <Spinner />
      ) : (
        <div className="flex flex-col lg:flex-row [&>*]:!grow gap-3">
          <Button className="button button-secondary" onClick={() => onCloseModal()}>
            Cancel
          </Button>

          <Button className="button button-primary" id="submit" disabled={isSubmitDisabled()} onClick={submitRequest}>
            Save
          </Button>
        </div>
      )}
    </div>
  );
};
